import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const products = [
  {
    id: 'cashier',
    title: 'PayXB Smart Checkout',
    description: 'A unified checkout experience that supports all payment methods and providers.',
    features: [
      'Single integration for all payment methods',
      'Smart routing for optimal success rates',
      'Real-time analytics and reporting',
      'Customizable checkout experience',
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    id: 'router',
    title: 'PayXB Router',
    description: 'AI-powered payment routing engine that maximizes transaction success rates.',
    features: [
      'Machine learning-based routing',
      'Real-time performance monitoring',
      'Automatic failover handling',
      'Custom routing rules',
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
  },
  {
    id: 'dashboard',
    title: 'PayXB Dashboard',
    description: 'Comprehensive analytics and management platform for your payment operations.',
    features: [
      'Real-time transaction monitoring',
      'Advanced reporting tools',
      'Customizable dashboards',
      'Automated alerts and notifications',
    ],
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
];

const Products: React.FC = () => {
  const [activeProduct, setActiveProduct] = useState(products[0]);

  return (
    <section id="products" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">Our Products</h2>
          <p className="section-subtitle">
            Comprehensive solutions for modern payment orchestration
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Product Navigation */}
          <div className="space-y-4">
            {products.map((product) => (
              <motion.button
                key={product.id}
                onClick={() => setActiveProduct(product)}
                className={`w-full p-6 rounded-xl text-left transition-all duration-300 ${
                  activeProduct.id === product.id
                    ? 'bg-black text-white shadow-3d'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    activeProduct.id === product.id ? 'bg-white text-black' : 'bg-black text-white'
                  }`}>
                    {product.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">{product.title}</h3>
                    <p className={`text-sm ${
                      activeProduct.id === product.id ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {product.description}
                    </p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Product Details */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeProduct.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-gray-50 rounded-2xl p-8 shadow-3d"
            >
              <h3 className="text-2xl font-bold mb-6">{activeProduct.title}</h3>
              <ul className="space-y-4">
                {activeProduct.features.map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-600">{feature}</span>
                  </motion.li>
                ))}
              </ul>
              <a href="#features" className="mt-8 btn-primary block text-center">
                Learn More
              </a>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Products; 